import {
  collection,
  getDocs,
  getFirestore,
  query,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  where,
  orderBy,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  type DocumentData,
  runTransaction,
  Timestamp,
  Transaction,
} from 'firebase/firestore';
import { useStore } from './index';
import type { Slot, SlotTypeBucket, SlotV2, SlotAssignment, RackAssignment } from './types/index';
import { getApp } from 'firebase/app';

// Define a new type for inventory slots that includes rack info and type name
export interface InventorySlot extends Slot {
  rackId: string;
  rackName: string | null;
  rackLocation?: string;
  typeName?: string; // Add optional typeName field
}

/**
 * Fetches all inventory slots for either the current user OR the current team,
 * including rack name, location, and resolved type name.
 */
export async function fetchInventorySlots(): Promise<InventorySlot[]> {
  const fbApp = getApp(this.firebaseApp);
  const store = useStore();
  const db = getFirestore(fbApp, this.dbid);
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    console.error('User not logged in.');
    return [];
  }

  let typeBuckets: SlotTypeBucket[] = [];
  // Ensure types are loaded before processing slots
  try {
    typeBuckets = await store.fetchTypes(); // Call store action, assumes it returns buckets
  } catch (error) {
    console.error('Error fetching types for inventory slots:', error);
    // Decide if you want to return [] or proceed without type names
    return []; // Returning empty for now if types fail to load
  }

  const inventorySlots: InventorySlot[] = [];
  let basePath: [string, string, string] | [string, string, string, string, string]; // Type for path segments

  if (userMode === 'team' && teamId) {
    basePath = ['teams', teamId, 'racks'];
  } else if (userMode === 'user') {
    basePath = ['users', userId, 'racks'];
  } else {
    console.error('Cannot fetch inventory slots: Invalid user mode or missing team ID.');
    return [];
  }

  // Helper function to find type label from buckets
  const findTypeName = (typeValue: string): string | undefined => {
    for (const bucket of typeBuckets) {
      const foundType = bucket.options.find(option => option.value === typeValue);
      if (foundType) {
        return foundType.label;
      }
    }
    return 'Invalid Type';
  };

  try {
    const racksCol = collection(db, ...basePath);
    const rackQuery = query(racksCol);
    const rackSnapshot = await getDocs(rackQuery);

    for (const rackDoc of rackSnapshot.docs) {
      const rackData = rackDoc.data();
      const rackId = rackDoc.id;
      const rackName = rackData.name || 'Unnamed Rack'; // Provide default rack name
      const rackLocation = rackData.location || undefined;

      // Check if slots array exists and is an array
      if (Array.isArray(rackData.slots)) {
        // Iterate through the slots array within the rack document
        rackData.slots.forEach((slot: Slot) => {
          // Find the type label using the helper function
          const typeName = findTypeName(slot.type);

          // Determine slot name (use slot.name or generate default)
          const slotName = slot.name || `${rackName} - Slot ${slot.slot ?? 'N/A'}`;
          // Determine slot id (use slot.id or generate default)
          const id = slot.id || `${rackId}-slot-${slot.slot ?? Math.random().toString(36).substring(2)}`;

          inventorySlots.push({
            id: id,
            name: slotName,
            serial: slot.serial || '',
            model: slot.model || '',
            size: slot.size || 0,
            type: slot.type,
            typeName: typeName,
            slot: slot.slot || null,
            notes: slot.notes || '',
            bodyColor: slot.bodyColor ?? null,
            rearType: slot.rearType ?? null,
            depth: slot.depth ?? null,
            depthUnit: slot.depthUnit || 'inches',
            powerConsumption: slot.powerConsumption ?? null,
            weight: slot.weight ?? null,
            weightUnit: slot.weightUnit || 'pounds',
            rackId: rackId,
            rackName: rackName,
            rackLocation: rackLocation,
          });
        });
      } else {
        // Optional: Log if a rack document doesn't have a slots array
        // console.warn(`Rack ${rackId} is missing or has an invalid slots array.`);
      }
    }
    return inventorySlots;
  } catch (error) {
    console.error(`Error fetching inventory slots for ${userMode} ${userMode === 'team' ? teamId : userId}:`, error);
    return []; // Return empty array on error
  }
}

export function generateV2SlotId(): string {
  const store = useStore();
  const db = getFirestore();

  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    throw new Error('User not logged in');
  }

  let basePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    basePath = ['teams', teamId, 'slots'];
  } else {
    basePath = ['users', userId, 'slots'];
  }

  const slotsCol = collection(db, ...basePath);
  const docRef = doc(slotsCol);

  return docRef.id;
}

// New v2 slot management functions
export interface CreateSlotOptions {
  name?: string;
  serial?: string;
  model?: string;
  size: number;
  type: string;
  notes?: string;
  tags?: string[];
  isInventory?: boolean;
  bodyColor?: string | null;
  rearType?: string | null;
  depth?: number | null;
  depthUnit?: 'inches' | 'centimeters';
  powerConsumption?: number | null;
  weight?: number | null;
  weightUnit?: 'pounds' | 'kilograms';
}

export interface UpdateSlotOptions {
  name?: string;
  serial?: string;
  model?: string;
  size?: number;
  type?: string;
  notes?: string;
  tags?: string[];
  isInventory?: boolean;
  bodyColor?: string | null;
  rearType?: string | null;
  depth?: number | null;
  depthUnit?: 'inches' | 'centimeters';
  powerConsumption?: number | null;
  weight?: number | null;
  weightUnit?: 'pounds' | 'kilograms';
}

/**
 * Creates a new slot in the centralized slot collection
 */
export async function createSlot(options: CreateSlotOptions): Promise<SlotV2> {
  const store = useStore();
  const db = getFirestore();
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    throw new Error('User not logged in');
  }

  const slotData: Omit<SlotV2, 'id' | 'createdAt' | 'updatedAt'> = {
    name: options.name || null,
    serial: options.serial || null,
    model: options.model || null,
    size: options.size,
    type: options.type,
    notes: options.notes || null,
    bodyColor: options.bodyColor ?? null,
    rearType: options.rearType ?? null,
    depth: options.depth ?? null,
    depthUnit: options.depthUnit || 'inches',
    powerConsumption: options.powerConsumption ?? null,
    weight: options.weight ?? null,
    weightUnit: options.weightUnit || 'pounds',
    tags: options.tags || null,
    createdBy: teamId || userId,
    isInventory: options.isInventory !== false, // Default to true
    rackAssignments: [],
  };

  let basePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    basePath = ['teams', teamId, 'slots'];
  } else {
    basePath = ['users', userId, 'slots'];
  }

  try {
    const now = new Date();
    const slotsCol = collection(db, ...basePath);
    const docRef = await addDoc(slotsCol, {
      ...slotData,
      createdAt: now,
      updatedAt: now,
    });

    return {
      id: docRef.id,
      ...slotData,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
    };
  } catch (error) {
    console.error('Error creating slot:', error);
    throw new Error('Failed to create slot');
  }
}

/**
 * Updates an existing slot
 */
export async function updateSlot(id: string, options: UpdateSlotOptions): Promise<void> {
  const store = useStore();
  const db = getFirestore();
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    throw new Error('User not logged in');
  }

  let basePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    basePath = ['teams', teamId, 'slots'];
  } else {
    basePath = ['users', userId, 'slots'];
  }

  try {
    const slotRef = doc(db, ...basePath, id);
    const updateData: Partial<SlotV2> = {
      updatedAt: Timestamp.fromDate(new Date()),
    };

    // Only update fields that are provided
    if (options.name !== undefined) updateData.name = options.name;
    if (options.serial !== undefined) updateData.serial = options.serial;
    if (options.model !== undefined) updateData.model = options.model;
    if (options.size !== undefined) updateData.size = options.size;
    if (options.type !== undefined) updateData.type = options.type;
    if (options.notes !== undefined) updateData.notes = options.notes;
    if (options.bodyColor !== undefined) updateData.bodyColor = options.bodyColor;
    if (options.rearType !== undefined) updateData.rearType = options.rearType;
    if (options.depth !== undefined) updateData.depth = options.depth;
    if (options.depthUnit !== undefined) updateData.depthUnit = options.depthUnit;
    if (options.powerConsumption !== undefined) updateData.powerConsumption = options.powerConsumption;
    if (options.weight !== undefined) updateData.weight = options.weight;
    if (options.weightUnit !== undefined) updateData.weightUnit = options.weightUnit;
    if (options.tags !== undefined) updateData.tags = options.tags;
    if (options.isInventory !== undefined) updateData.isInventory = options.isInventory;

    await updateDoc(slotRef, updateData);
  } catch (error) {
    console.error('Error updating slot:', error);
    throw new Error('Failed to update slot');
  }
}

/**
 * Deletes a slot (only if it's not assigned to any racks)
 */
export async function deleteSlot(id: string): Promise<void> {
  const store = useStore();
  const db = getFirestore();
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    throw new Error('User not logged in');
  }

  let basePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    basePath = ['teams', teamId, 'slots'];
  } else {
    basePath = ['users', userId, 'slots'];
  }

  const slotRef = doc(db, ...basePath, id);

  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(slotRef);
      if (!snap.exists()) {
        throw new Error('Slot not found');
      }

      const data = snap.data() as SlotV2;
      if (data.rackAssignments && data.rackAssignments.length > 0) {
        throw new Error('Cannot delete slot that is assigned to racks. Remove assignments first.');
      }

      tx.delete(slotRef);
    });
  } catch (error) {
    console.error('Error deleting slot:', error);
    throw error;
  }
}

/**
 * Fetches a single slot by ID
 */
export async function fetchSlot(id: string, signal?: AbortSignal): Promise<SlotV2 | null> {
  const store = useStore();
  const db = getFirestore();
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    throw new Error('User not logged in');
  }

  // Check if already aborted before making the call
  if (signal?.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }

  let basePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    basePath = ['teams', teamId, 'slots'];
  } else {
    basePath = ['users', userId, 'slots'];
  }

  try {
    const slotRef = doc(db, ...basePath, id);

    // Create an abort promise that rejects when signal is aborted
    const abortPromise = new Promise<never>((_, reject) => {
      if (signal) {
        signal.addEventListener('abort', () => {
          reject(new DOMException('The operation was aborted.', 'AbortError'));
        });
      }
    });

    // Race the getDoc call against the abort promise
    const slotDoc = await Promise.race([
      getDoc(slotRef),
      abortPromise,
    ]);

    // Check if aborted after the call
    if (signal?.aborted) {
      throw new DOMException('The operation was aborted.', 'AbortError');
    }

    if (!slotDoc.exists()) {
      return null;
    }

    return {
      id: slotDoc.id,
      ...slotDoc.data(),
    } as SlotV2;
  } catch (error) {
    // Don't log or throw abort errors - they're expected when requests are cancelled
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching slot:', error);
    throw new Error('Failed to fetch slot');
  }
}

/**
 * Fetches all inventory slots (v2 system)
 */
export async function fetchInventorySlotsV2(): Promise<SlotV2[]> {
  const store = useStore();
  const db = getFirestore();
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    console.error('User not logged in.');
    return [];
  }

  let basePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    basePath = ['teams', teamId, 'slots'];
  } else {
    basePath = ['users', userId, 'slots'];
  }

  try {
    const slotsCol = collection(db, ...basePath);
    const slotsQuery = query(
      slotsCol,
      where('isInventory', '==', true),
      orderBy('createdAt', 'desc'),
    );
    const slotsSnapshot = await getDocs(slotsQuery);

    return slotsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as SlotV2[];
  } catch (error) {
    console.error('Error fetching inventory slots v2:', error);
    return [];
  }
}

/**
 * Removes a slot assignment from a rack
 */
export async function removeSlotFromRack(slotId: string, rackId: string): Promise<void> {
  const store = useStore();
  const db = getFirestore();
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    throw new Error('User not logged in');
  }

  let slotsBasePath: [string, string, string] | [string, string, string, string, string];
  let racksBasePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    slotsBasePath = ['teams', teamId, 'slots'];
    racksBasePath = ['teams', teamId, 'racks'];
  } else {
    slotsBasePath = ['users', userId, 'slots'];
    racksBasePath = ['users', userId, 'racks'];
  }

  try {
    // Remove assignment from slot
    const slotRef = doc(db, ...slotsBasePath, slotId);
    const slotDoc = await getDoc(slotRef);

    if (!slotDoc.exists()) {
      throw new Error('Slot not found');
    }

    const slotData = slotDoc.data() as SlotV2;
    const updatedAssignments = slotData.rackAssignments?.filter(
      (assignment: RackAssignment) => assignment.rackId !== rackId,
    ) || [];

    await updateDoc(slotRef, {
      rackAssignments: updatedAssignments,
      updatedAt: new Date(),
    });

    // Remove assignment from rack
    const rackRef = doc(db, ...racksBasePath, rackId);
    const rackDoc = await getDoc(rackRef);

    if (rackDoc.exists()) {
      const rackData = rackDoc.data();
      const updatedSlotAssignments = rackData.slotAssignments?.filter(
        (assignment: SlotAssignment) => assignment.id !== slotId,
      ) || [];

      await updateDoc(rackRef, {
        slotAssignments: updatedSlotAssignments,
      });
    }
  } catch (error) {
    console.error('Error removing slot from rack:', error);
    throw error;
  }
}

/**
 * Assigns a slot to a rack
 */
export async function assignSlotToRack(
  slotId: string,
  rackId: string,
  slotPosition: number,
  tx?: Transaction,
): Promise<void> {
  const store = useStore();
  const db = getFirestore();
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    throw new Error('User not logged in');
  }

  let slotsBasePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    slotsBasePath = ['teams', teamId, 'slots'];
  } else {
    slotsBasePath = ['users', userId, 'slots'];
  }

  try {
    // Add assignment to slot
    const slotRef = doc(db, ...slotsBasePath, slotId);
    const slotDoc = await getDoc(slotRef);

    if (!slotDoc.exists()) {
      throw new Error('Slot not found');
    }

    const slotData = slotDoc.data() as SlotV2;
    const updatedAssignments = [...slotData.rackAssignments, {
      rackId: rackId,
      slot: slotPosition,
      assignedAt: new Date(),
      assignedBy: userId,
    }];

    if (tx) {
      tx.update(slotRef, {
        rackAssignments: updatedAssignments,
        updatedAt: new Date(),
      });
    } else {
      await updateDoc(slotRef, {
        rackAssignments: updatedAssignments,
        updatedAt: new Date(),
      });
    }
  } catch (error) {
    console.error('Error assigning slot to rack:', error);
    throw error;
  }
}

/**
 * Searches slots with pagination support
 */
export interface SearchSlotsOptions {
  query?: string;
  type?: string;
  size?: number;
  tags?: string[];
  limit?: number;
  startAfter?: QueryDocumentSnapshot<DocumentData>;
}

export async function searchSlots(options: SearchSlotsOptions = {}): Promise<{
  slots: SlotV2[];
  hasMore: boolean;
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
}> {
  const store = useStore();
  const db = getFirestore();
  const userId = store.user?.uid;
  const userMode = store.userMode;
  const teamId = userMode === 'team' ? store.team : null;

  if (!userId) {
    throw new Error('User not logged in');
  }

  let basePath: [string, string, string] | [string, string, string, string, string];

  if (userMode === 'team' && teamId) {
    basePath = ['teams', teamId, 'slots'];
  } else {
    basePath = ['users', userId, 'slots'];
  }

  try {
    const slotsCol = collection(db, ...basePath);
    let slotsQuery = query(slotsCol, orderBy('createdAt', 'desc'));

    // Apply filters
    if (options.type) {
      slotsQuery = query(slotsQuery, where('type', '==', options.type));
    }
    if (options.size) {
      slotsQuery = query(slotsQuery, where('size', '==', options.size));
    }

    // Apply pagination
    if (options.limit) {
      slotsQuery = query(slotsQuery, limit(options.limit));
    }
    if (options.startAfter) {
      slotsQuery = query(slotsQuery, startAfter(options.startAfter));
    }

    // Apply server-side filters
    if (options.tags && options.tags.length > 0) {
      slotsQuery = query(slotsQuery, where('tags', 'array-contains-any', options.tags));
    }

    // Apply pagination
    const queryLimit = options.limit || 20;
    slotsQuery = query(slotsQuery, limit(queryLimit + 1)); // Fetch one extra to check for more

    if (options.startAfter) {
      slotsQuery = query(slotsQuery, startAfter(options.startAfter));
    }

    const slotsSnapshot = await getDocs(slotsQuery);
    let slots = slotsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as SlotV2[];

    const hasMore = slots.length > queryLimit;
    if (hasMore) {
      slots = slots.slice(0, queryLimit); // Remove the extra item
    }

    // Apply client-side text search filter if provided
    // Apply text search filter if provided (client-side for now)
    let filteredSlots = slots;
    if (options.query) {
      const queryLower = options.query.toLowerCase();
      filteredSlots = slots.filter(slot =>
        slot.name?.toLowerCase().includes(queryLower) ||
        slot.serial?.toLowerCase().includes(queryLower) ||
        slot.model?.toLowerCase().includes(queryLower) ||
        slot.notes?.toLowerCase().includes(queryLower),
      );
    }

    const lastDoc = slotsSnapshot.docs[slots.length - 1];

    return {
      slots: filteredSlots,
      hasMore,
      lastDoc,
    };
  } catch (error) {
    console.error('Error searching slots:', error);
    throw new Error('Failed to search slots');
  }
}
